/**
 * constants.js — 견적서 관리 공통 상수 및 유틸 함수 모음
 *
 * 이 파일은 quotemgmt/ 폴더의 모든 모듈에서 공유하는
 * 회사 정보, 약관, 상품 옵션, 날짜/숫자 포맷 유틸을 export 합니다.
 */

import { todayKST, toDateString } from '../../../utils/date';

// ─── 고정 회사 정보 ───────────────────────────────────────────────────────────
// 인쇄/PDF 견적서의 '공급자' 패널에 표시되는 파인그룹 법인 정보
export const COMPANY = {
  bizNum:  '345-87-01596',
  name:    '주식회사 파인그룹',
  ceo:     '정명철',
  address: '서울특별시 강서구 마곡중앙1로 10, 한일노벨리아타워 719~722호',
  bizType: '여행업, 통신판매업',
  bizKind: '국내여행업, 전자상거래',
  phone:   '1522-6419',
  bank:    '국민은행 649301-04-089106 ㈜ 파인그룹',
};

// ─── 견적서 약관 문구 (7개 항목) ─────────────────────────────────────────────
// 인쇄/PDF 하단에 번호 순서대로 출력됩니다.
export const TERMS = [
  '공급하는 자는 공급받는 자에게 계약기간 내 접수되는 상기 구매상품에 대해 공급을 책임져야 하며, 이를 수행하지 못하여 발생되는 문제가 있을 경우 공급받는 자는 공급하는 자에게 손해배상을 청구 할 수 있다.',
  '공급받는 자는 상기 구매상품을 고객에게 행사목적으로 무상 지급하여야 한다. 상행위 이외의 목적으로 사용하거나, 당사가 규정한 용도 이외의 방법으로 사용할 시 행사는 자동으로 취소되며 이에 따른 모든 책임은 공급받는 자에게 있다.',
  '공급받는 자는 공급하는 자의 행사내용과 관계없는 과장된 내용을 고객에게 전달하여 주식회사 파인그룹에 피해를 입혔을 시 그 책임을 공급받는 자가 진다. (위 내용의 반대일 경우 책임은 공급하는 자가 진다.)',
  '본 행사상품의 사용방법은 본사 홈페이지 및 상품권 후면 "이용안내"를 참조한다.',
  '본 견적서는 계약서 및 영수증으로써의 법적효력을 가지며, 행사특성상 상호 비밀유지의 책임이 있다.',
  '본 계약과 관련하여 분쟁 발생시 성실과 신의로 조정하며, 법적문제 발생 시 관할 법원은 공급하는 자의 소재지로 한다.',
  '본 계약을 증명하기 위하여 공급받는 자의 사업자등록증을 받아 견적서를 작성하여 발송 한다.',
];

// ─── 품목 선택 옵션 ───────────────────────────────────────────────────────────
// 견적서 폼의 품목1/품목2 <select> 에서 사용
export const PRODUCT_OPTIONS = ['조식VIP 제주여행권', '제주도여행권'];

// ─── 숫자 포맷 유틸 ───────────────────────────────────────────────────────────

/**
 * 숫자 또는 숫자 문자열을 3자리 콤마 포맷으로 변환
 * fmtComma(10000) → "10,000"
 */
export function fmtComma(v) {
  const d = String(v || '').replace(/\D/g, '');
  return d ? Number(d).toLocaleString('ko-KR') : '';
}

/**
 * 콤마 포함 문자열을 정수로 변환
 * parseComma("10,000") → 10000
 * 파싱 실패 시 null 반환
 */
export function parseComma(v) {
  const n = parseInt(String(v || '').replace(/,/g, ''), 10);
  return isNaN(n) ? null : n;
}

/**
 * 사업자등록번호 입력 자동 포맷: 숫자만 받아 XXX-XX-XXXXX 형식으로 반환
 */
export function fmtBizNum(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
}

// ─── 날짜 유틸 ────────────────────────────────────────────────────────────────

/** 오늘 날짜를 ISO 형식 문자열(YYYY-MM-DD)로 반환 (KST 기준) */
export const today = todayKST;

/**
 * 날짜 문자열을 한국식(년 월 일) 형식으로 변환
 * fmtKorDate("2025-04-01") → "2025년 04월 01일"
 */
export function fmtKorDate(d) {
  if (!d) return '';
  const [y, m, day] = toDateString(d).split('-');
  return `${y}년 ${m}월 ${day}일`;
}

// ─── 한글 금액 변환 ───────────────────────────────────────────────────────────

/**
 * 숫자를 한글 금액 문자열로 변환
 * numToKoreanWon(10000) → "일만 원"
 */
export function numToKoreanWon(amount) {
  if (!amount) return '영 원';
  const ones  = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const units = ['', '십', '백', '천'];
  const big   = ['', '만', '억', '조'];

  // 4자리 그룹 단위를 한글로 변환
  function g(n) {
    let s = '';
    for (let i = 3; i >= 0; i--) {
      const d = Math.floor(n / Math.pow(10, i)) % 10;
      if (d) s += ones[d] + units[i];
    }
    return s;
  }

  let result = '';
  let num    = amount;
  let bi     = 0;
  while (num > 0) {
    const grp = num % 10000;
    if (grp) result = g(grp) + big[bi] + result;
    num = Math.floor(num / 10000);
    bi++;
  }
  return result + ' 원';
}

// ─── 무상제공품목 직렬화/역직렬화 ────────────────────────────────────────────

/**
 * DB에 저장된 무상제공품목 문자열을 [{name, qty}] 배열로 파싱
 * - JSON 배열 형식 우선 시도
 * - 실패 시 구버전 쉼표 구분 문자열로 폴백 처리
 */
export function parseFreeItems(str) {
  if (!str) return [];
  try {
    const p = JSON.parse(str);
    if (Array.isArray(p)) {
      return p.map((r) => ({ name: r.name || '', qty: fmtComma(r.qty || 1) }));
    }
  } catch (_) {
    // 구버전: 쉼표 구분 문자열 → 각 항목의 qty를 1로 설정
    return str.split(',').map((s) => s.trim()).filter(Boolean).map((name) => ({ name, qty: '1' }));
  }
  return [];
}

/**
 * [{name, qty}] 배열을 DB 저장용 JSON 문자열로 직렬화
 * 빈 항목 제거 후 qty는 숫자로 변환
 * 유효 항목이 없으면 null 반환
 */
export function serializeFreeItems(rows) {
  const v = rows.filter((r) => r.name.trim());
  if (!v.length) return null;
  return JSON.stringify(v.map((r) => ({ name: r.name.trim(), qty: parseComma(r.qty) || 1 })));
}

// ─── 빈 폼 초기값 ─────────────────────────────────────────────────────────────

/**
 * 견적서 등록 폼의 초기 상태 객체를 반환
 * @param {string} nextNum — 자동 채번된 다음 문서번호 (기본 빈 문자열)
 */
export function emptyForm(nextNum = '') {
  return {
    quote_number:        nextNum,
    issue_date:          today(),
    requester_name:      '',
    recipient_name:      '',
    business_number:     '',
    product1_name:       '',
    product1_qty:        '',
    product1_unit_price: '',
    product2_name:       '',
    product2_qty:        '',
    vat_yn:              true,
    memo:                '',
  };
}

// ─── 공통 UI 스타일 (PromotionalManagement 패턴) ─────────────────────────────
// 목록/폼/프린트 모듈에서 공통으로 사용하는 인라인 스타일 상수

export const pageHeaderCard = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
export const pageTitleStyle = { margin: 0, fontSize: 20, fontWeight: 900, color: 'var(--text)' };
export const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
export const cardHeaderStyle = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', marginBottom: 14,
};
export const cardTitleStyle = { fontSize: 14, fontWeight: 800, color: 'var(--text-sub)' };

// 버튼 스타일
export const btnPrimary = {
  border: 'none', borderRadius: 8, padding: '8px 16px',
  background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
  color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
export const btnSecondary = {
  border: '1.5px solid var(--border)', borderRadius: 8, padding: '7px 14px',
  background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
export const btnDanger = {
  border: '1.5px solid #fca5a5', borderRadius: 8, padding: '7px 12px',
  background: 'var(--bg-card)', color: '#dc2626', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
export const inputStyle = {
  width: '100%', border: '1px solid var(--border)', borderRadius: 6,
  padding: '9px 10px', fontSize: 14, boxSizing: 'border-box',
  outline: 'none', background: 'var(--bg-card)',
};
export const selectStyle = { ...inputStyle, cursor: 'pointer' };
export const labelStyle = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
  marginBottom: 4, display: 'block',
};
